import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw, Search, Unlock } from 'lucide-react';
import { api } from '../lib/api';
import { EXPLORER_URL } from '../lib/network';
import { formatDate, formatToken, shortenAddress } from '../lib/format';
import { UTILITY_TOKEN_ABI, resolveGovernanceAddresses, sendGovernanceCall } from '../lib/governance';
import { atomicToDecimal } from '../state/AppDataContext';
import { useWallet } from '../state/WalletContext';
import { TokenLiquiditySummary, btcValue, burnProgress, useTokenLiquidity } from './TokenLiquidityInfo';
import { Pager } from './Pager';

/*
 * Token unfreeze report (owner request 2026-09-30; rule 14 / rule 29). Every Primary Token with its freeze window,
 * readiness, the unfreeze transaction, the full on-chain state history and the final outcome (held, converted to CDTT,
 * liquidated, burned). Data: GET /api/<slug>/token-unfreeze-report (finalized chain events only).
 */
interface HistoryEntry { from: string; to: string; at: string | null; txHash: string }
interface Row {
  bdId: string; tokenLabel: string; wallet: string; issueEra: string; originalRatioBps: number; currentRatioBps: number; liquidityAtomic: string;
  issuedAt: string | null; unfreezeDueAt: string | null; status: string; statusCode: number; bucket: string; unfreezeReady: boolean;
  secondsUntilUnfreeze: number | null; unfrozenAt: string | null; unfreezeTxHash: string | null; frozenForSeconds: number | null;
  outcome: string; outcomeAt: string | null; outcomeTxHash: string | null; outcomeAmountAtomic: string | null; earningsAtBurnAtomic: string | null; history: HistoryEntry[];
}
interface Report {
  generatedAt: string; freezeSeconds: number; freezeIsDefault: boolean; nextDueAt: string | null;
  totals: { members: number; minted: number; frozenNotDue: number; ready: number; unfrozen: number; redemptionPending: number; converted: number; liquidated: number; burned: number };
  items: Row[];
}
const FILTERS: Array<{ key: string; label: string }> = [
  { key: 'all', label: 'All' }, { key: 'frozen-not-due', label: 'Frozen (period running)' }, { key: 'unfrozen', label: 'Unfrozen (held)' },
  { key: 'redemption-pending', label: 'Redemption pending' }, { key: 'converted', label: 'Converted (Option B / legacy CDTT)' }, { key: 'liquidated', label: 'Liquidated' }, { key: 'burned', label: 'Burned' },
];
const btc = (atomic: string | null) => (atomic ? formatToken(atomicToDecimal(atomic)) : '—');
const ratio = (bps: number) => `${bps / 10_000}X`;
const tx = (hash: string | null) => hash ? <a href={`${EXPLORER_URL}/tx/${hash}`} target="_blank" rel="noreferrer" className="tx-link">{hash.slice(0, 10)}…</a> : <>—</>;
function duration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 90) return `${seconds} s`;
  if (seconds < 5_400) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172_800) return `${(seconds / 3_600).toFixed(1)} h`;
  return `${(seconds / 86_400).toFixed(1)} days`;
}

export function AdminTokenUnfreezeReport({ slug }: { slug: string }) {
  const wallet = useWallet();
  const tokenLiquidity = useTokenLiquidity();
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const load = useCallback(async () => {
    setError(null);
    try { setReport(await api<Report>(`/api/${slug}/token-unfreeze-report`)); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Token unfreeze report could not be loaded.'); }
  }, [slug]);
  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => {
    if (!report) return [];
    const q = query.trim().toLowerCase();
    return report.items.filter((row) => (filter === 'all' || row.bucket === filter) && (!q || row.bdId.toLowerCase().includes(q) || row.tokenLabel.toLowerCase().includes(q) || row.wallet.toLowerCase().includes(q) || row.status.toLowerCase().includes(q) || row.outcome.toLowerCase().includes(q)));
  }, [report, filter, query]);

  async function unfreeze(row: Row) {
    if (!wallet.address) { setError('Connect any wallet to send the unfreeze transaction (gas only).'); return; }
    setBusyId(row.bdId); setError(null); setNotice(null);
    try {
      const hash = await sendGovernanceCall(resolveGovernanceAddresses(), wallet.address as `0x${string}`, 'token', UTILITY_TOKEN_ABI, 'unfreeze', [BigInt(row.bdId.replace(/^BD-/i, ''))]);
      setNotice(`Unfreeze of ${row.tokenLabel} sent: ${String(hash).slice(0, 12)}… - the report updates once the indexer sees the block.`);
      setTimeout(() => void load(), 15_000);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unfreeze failed.');
    } finally {
      setBusyId(null);
    }
  }

  function exportCsv() {
    if (!report) return;
    const header = ['Token', 'ID', 'Wallet', 'Era', 'Original ratio', 'Current ratio', 'Liquidity (BTCB)', 'Burn %', 'Issued (freeze start)', 'Unfreeze due', 'Status', 'Ready to unfreeze', 'Unfrozen on', 'Unfreeze tx', 'Frozen for (s)', 'Outcome', 'Outcome on', 'Outcome amount (BTCB)', 'Outcome tx', 'Earnings at burn (BTCB)', 'History'];
    const cell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const lines = rows.map((row) => [row.tokenLabel, row.bdId, row.wallet, row.issueEra, ratio(row.originalRatioBps), ratio(row.currentRatioBps), atomicToDecimal(row.liquidityAtomic), burnProgress(row.originalRatioBps, row.currentRatioBps, row.bucket === 'burned'), row.issuedAt, row.unfreezeDueAt, row.status, row.unfreezeReady ? 'yes' : 'no', row.unfrozenAt, row.unfreezeTxHash, row.frozenForSeconds, row.outcome, row.outcomeAt, row.outcomeAmountAtomic ? atomicToDecimal(row.outcomeAmountAtomic) : '', row.outcomeTxHash, row.earningsAtBurnAtomic ? atomicToDecimal(row.earningsAtBurnAtomic) : '', row.history.map((entry) => `${entry.from}>${entry.to}@${entry.at ?? ''}`).join(' | ')].map(cell).join(','));
    const blob = new Blob([[header.map(cell).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob); link.download = 'bdynamic-token-unfreeze-report.csv'; link.click();
    URL.revokeObjectURL(link.href);
  }

  const totals = report?.totals;
  return (
    <section className="panel audit-table neon-panel" id="admin-token-unfreeze">
      <div className="panel-heading">
        <div><span className="eyebrow">TOKEN UNFREEZE REPORT (RULE 14 · RULE 29)</span><h2><Unlock size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Freeze window, unfreeze and outcome of every Primary Token</h2></div>
        <div>
          <button className="button ghost small" type="button" onClick={() => void load()}><RefreshCw size={14} /> Refresh</button>{' '}
          <button className="button ghost small" type="button" disabled={!rows.length} onClick={exportCsv}><Download size={14} /> Export CSV</button>
        </div>
      </div>
      {error ? <p className="token-identity-error">{error}</p> : null}
      {notice ? <p className="empty-copy">{notice}</p> : null}
      {report && totals ? (
        <>
          <p className="empty-copy">
            Freeze period in force: <strong>{duration(report.freezeSeconds)}</strong> ({report.freezeSeconds.toLocaleString()} s{report.freezeIsDefault ? ', the production default of 365 days' : ', governance override for testing - production is 365 days'}). Unfreeze due = issue time + this period.
            A token unfreezes AUTOMATICALLY when its period ends (no transaction needed); from then on its holder settles once in the CDT Benefit card (Option A Token Liquidity in BTCB, or Option B CDT). Next token due: {report.nextDueAt ? formatDate(report.nextDueAt) : 'none pending'} · generated {formatDate(report.generatedAt)}.
          </p>
          <TokenLiquiditySummary />
          <dl className="primary-token-grid">
            <div><dt>IDs / tokens minted</dt><dd>{totals.members} / {totals.minted}</dd></div>
            <div><dt>Frozen (not due yet)</dt><dd>{totals.frozenNotDue}</dd></div>
            <div><dt>Unfrozen and held</dt><dd>{totals.unfrozen}</dd></div>
            <div><dt>Redemption pending</dt><dd>{totals.redemptionPending}</dd></div>
            <div><dt>Converted (Option B / legacy CDTT)</dt><dd>{totals.converted}</dd></div>
            <div><dt>Liquidated for BTCB</dt><dd>{totals.liquidated}</dd></div>
            <div><dt>Burned (earnings tier)</dt><dd>{totals.burned}</dd></div>
          </dl>
          <div className="pt-filter">
            <label><Search size={14} /><input placeholder="Filter by ID, token, wallet, status or outcome" value={query} onChange={(event) => setQuery(event.target.value)} spellCheck={false} /></label>
          </div>
          <p className="empty-copy">
            {FILTERS.map((item) => <button key={item.key} type="button" className={`button ${filter === item.key ? 'primary' : 'ghost'} small`} style={{ marginRight: 6, marginBottom: 6 }} onClick={() => { setFilter(item.key); setPage(0); }}>{item.label}</button>)}
            {rows.length} of {report.items.length} tokens match.
          </p>
          <Pager page={page} pageSize={pageSize} total={rows.length} onPage={setPage} onPageSize={setPageSize} />
          <div className="pt-table-wrap">
            <table className="pt-table">
              <thead><tr><th>Token</th><th>ID</th><th>Wallet</th><th>Era · ratio</th><th>Burn %</th><th>Live value</th><th>Issued (freeze start)</th><th>Unfreeze due</th><th>Status</th><th>Unfrozen on</th><th>Frozen for</th><th>Outcome</th><th>Action / history</th></tr></thead>
              <tbody>
                {rows.slice(Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1)) * pageSize, (Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1)) + 1) * pageSize).map((row) => (
                  <Fragment key={row.bdId}>
                    <tr>
                      <td><strong>{row.tokenLabel}</strong></td>
                      <td>{row.bdId}</td>
                      <td title={row.wallet}>{shortenAddress(row.wallet, 5)}</td>
                      <td>{row.issueEra} · {ratio(row.originalRatioBps)}{row.bucket !== 'burned' && row.currentRatioBps !== row.originalRatioBps ? ` → ${ratio(row.currentRatioBps)}` : ''}</td>
                      <td>{burnProgress(row.originalRatioBps, row.currentRatioBps, row.bucket === 'burned')}</td>
                      <td>{['burned', 'liquidated', 'converted'].includes(row.bucket) ? '—' : btcValue(tokenLiquidity?.valueFor(row.currentRatioBps) ?? null)}</td>
                      <td>{row.issuedAt ? formatDate(row.issuedAt) : '—'}</td>
                      <td>{row.unfreezeDueAt ? formatDate(row.unfreezeDueAt) : '—'}{row.statusCode === 1 && !row.unfreezeReady ? ` (in ${duration(row.secondsUntilUnfreeze)})` : ''}</td>
                      <td style={{ color: row.unfreezeReady ? '#f5c451' : row.bucket === 'burned' ? '#ff8a8a' : row.bucket === 'unfrozen' ? '#43dea4' : undefined, fontWeight: 600 }}>{row.unfreezeReady ? 'Frozen · READY' : row.status}</td>
                      <td>{row.unfrozenAt ? <>{formatDate(row.unfrozenAt)} {tx(row.unfreezeTxHash)}</> : '—'}</td>
                      <td>{duration(row.frozenForSeconds)}</td>
                      <td>{row.outcome}{row.outcomeAmountAtomic ? ` · ${btc(row.outcomeAmountAtomic)}` : ''}{row.earningsAtBurnAtomic ? ` · earnings ${btc(row.earningsAtBurnAtomic)}` : ''}{row.outcomeAt ? ` · ${formatDate(row.outcomeAt)}` : ''} {row.outcomeTxHash ? tx(row.outcomeTxHash) : null}</td>
                      <td>
                        {row.unfreezeReady ? <button className="button primary small" type="button" disabled={busyId !== null} onClick={() => void unfreeze(row)}>{busyId === row.bdId ? 'Confirm…' : 'Unfreeze'}</button> : null}{' '}
                        <button className="button ghost small" type="button" onClick={() => setOpen(open === row.bdId ? null : row.bdId)}>{open === row.bdId ? 'Hide' : `History (${row.history.length})`}</button>
                      </td>
                    </tr>
                    {open === row.bdId ? (
                      <tr>
                        <td colSpan={13}>
                          <strong>{row.tokenLabel} · on-chain state history</strong> · liquidity reference {btc(row.liquidityAtomic)} · wallet {row.wallet}
                          {row.history.length ? (
                            <table className="pt-table"><thead><tr><th>#</th><th>From</th><th>To</th><th>When</th><th>Tx</th></tr></thead><tbody>
                              {row.history.map((entry, index) => <tr key={index}><td>{index + 1}</td><td>{entry.from}</td><td>{entry.to}</td><td>{entry.at ? formatDate(entry.at) : '—'}</td><td>{tx(entry.txHash)}</td></tr>)}
                            </tbody></table>
                          ) : <p className="empty-copy">No state change recorded yet.</p>}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {!rows.length ? <p className="empty-copy">No token matches this filter.</p> : null}
        </>
      ) : !error ? <p className="empty-copy">Reading the token unfreeze report…</p> : null}
    </section>
  );
}
